import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, ArrowUpRight } from "lucide-react";
import { VERTICALS, NAV_LINKS, PHONE_DISPLAY, PHONE_TEL } from "@/lib/brand";

const Logo = () => (
  <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 group">
    <span className="flex h-9 w-9 items-center justify-center bg-ink text-white font-heading font-extrabold text-sm tracking-tight">
      OTB
    </span>
    <span className="font-heading font-bold text-ink text-lg leading-none tracking-tight">
      On The Brink
    </span>
  </Link>
);

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [vertOpen, setVertOpen] = useState(false);

  return (
    <header className="fixed top-0 inset-x-0 z-50 border-b border-slate-200 bg-white/75 backdrop-blur-xl">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Logo />

        <div className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.filter((l) => l.label !== "Verticals").map((l) => (
            <a key={l.label} href={l.href} data-testid={`nav-${l.label.toLowerCase()}`}
               className="text-sm font-medium text-slatey hover:text-ink transition-colors">
              {l.label}
            </a>
          ))}

          <div className="relative" onMouseEnter={() => setVertOpen(true)} onMouseLeave={() => setVertOpen(false)}>
            <button data-testid="nav-verticals-trigger"
                    className="flex items-center gap-1 text-sm font-medium text-slatey hover:text-ink transition-colors">
              Verticals <ChevronDown className="h-3.5 w-3.5" />
            </button>
            <AnimatePresence>
              {vertOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.15 }}
                  className="absolute right-0 top-full pt-3 w-80">
                  <div className="border border-slate-200 bg-white shadow-lg">
                    {VERTICALS.map((v) => (
                      <a key={v.id} href={v.url} target={v.url !== "#" ? "_blank" : undefined} rel="noreferrer"
                         data-testid={`nav-vertical-${v.id}`}
                         className="flex items-start justify-between gap-3 px-4 py-3 border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors group">
                        <div>
                          <div className="text-sm font-semibold text-ink">{v.name}</div>
                          <div className="text-xs text-slatey">{v.tag}</div>
                        </div>
                        <ArrowUpRight className="h-4 w-4 text-slate-300 group-hover:text-ember transition-colors" />
                      </a>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        <div className="hidden lg:flex items-center gap-3">
          <a href={PHONE_TEL} data-testid="nav-phone"
             className="text-sm font-semibold text-ink hover:text-ember transition-colors px-2">
            {PHONE_DISPLAY}
          </a>
          <Link to="/login" data-testid="nav-login"
                className="text-sm font-semibold text-ink hover:text-ember transition-colors px-3 py-2">
            Client Portal
          </Link>
          <Link to="/contact" data-testid="nav-contact-cta"
                className="text-sm font-semibold bg-ember text-white px-4 py-2.5 hover:bg-ember-dark transition-colors">
            Get in Touch
          </Link>
        </div>

        <button className="lg:hidden text-ink" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }}
                      className="lg:hidden overflow-hidden border-t border-slate-200 bg-white">
            <div className="px-6 py-4 flex flex-col gap-1">
              {NAV_LINKS.map((l) => (
                <a key={l.label} href={l.href} onClick={() => setOpen(false)}
                   className="py-2.5 text-sm font-medium text-slatey">{l.label}</a>
              ))}
              <Link to="/login" onClick={() => setOpen(false)} className="py-2.5 text-sm font-semibold text-ink">Client Portal</Link>
              <Link to="/contact" onClick={() => setOpen(false)}
                    className="mt-2 text-center text-sm font-semibold bg-ember text-white px-4 py-3">Get in Touch</Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
